import React, { useEffect, useState, useCallback } from 'react';
import { useNoteMode } from '@/contexts/NoteModeContext';
import { useConversation } from '@/contexts/ConversationContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { buildApiUrl } from '@/utils/apiClient';
import { getTabSessionToken } from '@/utils/tabSession';

/**
 * Connection status types for AI health.
 */
type AiStatus = 'connected' | 'degraded' | 'disconnected';

function SessionIndicators() {
  const noteModeContext = useNoteMode();
  const conversationContext = useConversation();
  const isMobile = useIsMobile();
  const [aiStatus, setAiStatus] = useState<AiStatus>('connected');
  const [aiStatusDetail, setAiStatusDetail] = useState<string | null>(null);
  
  const { noteMode } = noteModeContext || { noteMode: false };
  const { currentSessionId } = conversationContext || { currentSessionId: null };

  const checkAiHealth = useCallback(async () => {
    try {
      // Use raw fetch to avoid fetchWithAuth's 5xx toast — we handle errors via the status indicator.
      // Still send the tab-session token so the request clears RoleSessionIsolationMiddleware;
      // without it the probe is 403'd before /api/health/ai runs and can never report real health.
      const tabToken = getTabSessionToken();
      // eslint-disable-next-line no-restricted-globals
      const res = await fetch(buildApiUrl('/api/health/ai'), {
        credentials: 'include',
        headers: tabToken ? { 'X-Tab-Session': tabToken } : undefined,
      });
      // A non-2xx response (auth blip / transient 5xx) means we could not
      // determine AI health — that is NOT the same as the AI being degraded, so
      // don't raise a false "AI Degraded" alarm; leave the current status until
      // a probe resolves it.
      if (!res.ok) {
        return;
      }
      const data = await res.json();
      if (data && data.ok === true) {
        setAiStatus('connected');
        setAiStatusDetail(null);
      } else if (data && data.ok === false) {
        // Server affirmatively reports the AI backend is degraded.
        setAiStatus('degraded');
        setAiStatusDetail(data.where || 'unknown');
      }
      // A payload without a boolean `ok` field is undeterminable — leave the
      // current status rather than flashing a false "AI Degraded (unknown)".
    } catch {
      setAiStatus('disconnected');
      setAiStatusDetail('Unable to reach server');
    }
  }, []);

  useEffect(() => {
    checkAiHealth();
    const interval = setInterval(checkAiHealth, 30000);
    return () => clearInterval(interval);
  }, [checkAiHealth]);

  // Listen for online/offline events to update status immediately
  useEffect(() => {
    const handleOnline = () => checkAiHealth();
    const handleOffline = () => {
      setAiStatus('disconnected');
      setAiStatusDetail('No internet connection');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkAiHealth]);

  const statusConfig = {
    connected: { color: 'bg-green-500', label: 'AI Connected', textClass: 'text-green-700 dark:text-green-300', bgClass: 'bg-green-50 dark:bg-green-900/30' },
    degraded: { color: 'bg-amber-500', label: 'AI Degraded', textClass: 'text-amber-700 dark:text-amber-300', bgClass: 'bg-amber-50 dark:bg-amber-900/30' },
    disconnected: { color: 'bg-red-500', label: 'AI Offline', textClass: 'text-red-700 dark:text-red-300', bgClass: 'bg-red-50 dark:bg-red-900/30' },
  };

  const config = statusConfig[aiStatus];

  return (
    <>
      {/* AI Connection Status Indicator */}
      {aiStatus !== 'connected' && (
        <div className={`flex items-center justify-center gap-2 px-3 py-1.5 ${config.bgClass} rounded-lg mb-2`}
          title={aiStatusDetail || config.label}
        >
          <span className={`w-2 h-2 rounded-full ${config.color} ${aiStatus === 'degraded' ? 'animate-pulse' : ''}`} />
          <span className={`text-xs font-medium ${config.textClass}`}>{config.label}</span>
          {aiStatusDetail && (
            <span className={`text-xs ${config.textClass} opacity-75`}>({aiStatusDetail})</span>
          )}
          <button
            onClick={checkAiHealth}
            className={`text-xs ${config.textClass} hover:underline ml-1`}
            title="Check connection"
          >
            Retry
          </button>
        </div>
      )}

      {/* Note Mode Indicator - Hidden on mobile */}
      {noteMode && !isMobile && (
        <div className="absolute top-2 right-2 z-30 flex items-center bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full">
          <span className="mr-1">🪶</span>
          <span>Note Mode</span>
        </div>
      )}
      
      {/* DEV MODE Session ID Indicator - Hidden on mobile */}
      {import.meta.env.VITE_DEV_MODE === "true" && currentSessionId && !isMobile && (
        <div className="absolute top-2 left-2 z-30 flex items-center bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs px-2 py-1 rounded-full">
          <span className="mr-1">🔧</span>
          <span>Session ID: {currentSessionId.substring(0, 8)}...</span>
        </div>
      )}
    </>
  );
}

export default SessionIndicators;
